import Vue from "vue";

import { enableSpindleSensor } from "@/common/config";
import { v4 as uuidv4 } from "uuid";

import streamApi from "@/api/streamApi";

import { getFamily, isCmsMachine, findNode } from "./machineComponentsHelper";

const addSpindleSensor = async (components, machine, result) => {
  if (components) {
    result = { ...components };

    const isCms = isCmsMachine(machine);
    const family = getFamily(machine);
    const index = [
      "cms",
      "beam_saw",
      "sez",
      "bor",
      "boring",
      "boring_machine",
      "eba",
      "edge_bander"
    ].indexOf(family);
    let addSensor = false;

    if (!isCms && index < 0) {
      try {
        const hasChannels = await streamApi.getChannels(machine.Id, "Machine/Spindle/+");
        addSensor = hasChannels.length > 0;
      } catch (e) {
        console.error("[Error] addSpindleSensor error:", e);
      }

      if (addSensor) {
        const operatingGroupsNodesSpindle = findNode("SPINDLE", components, {
          field: "value",
          originalNode: true,
          all: true
        });

        const nodeSpindle =
          operatingGroupsNodesSpindle &&
          operatingGroupsNodesSpindle.length > 0 &&
          operatingGroupsNodesSpindle
            .sort((a, b) => {
              return (a.topic[0] || "").localeCompare(b.topic[0]);
            })
            .filter(el => (el.topic[0] || "").startsWith("Machine/OperatingGroups/"))[0];

        let loadActiveTimeNodes = [];

        if (nodeSpindle) {
          const regex = /Machine\/OperatingGroups\/[\d]*\//i;
          const nodeTopic = nodeSpindle.topic[0];
          let prefix = undefined;
          if (nodeTopic) {
            prefix = nodeTopic.match(regex)[0];
          }

          if (prefix) {
            const loadTopic = `${prefix}Load`;
            const activeTimeTopic = `${prefix}ActiveTime`;

            loadActiveTimeNodes = [
              {
                key: "Variable",
                title: "Load",
                topicLevel: "SpindleLoad",
                topic: [loadTopic],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "ActiveTime",
                topicLevel: "SpindleActiveTime",
                topic: [activeTimeTopic],
                id: uuidv4(),
                node: {}
              }
            ];
          }
        }

        const spindleSection = {
          key: "Component",
          title: "Spindle Sensor",
          topicLevel: "Spindle_Sensor",
          topic: [],
          id: uuidv4(),
          node: {},
          children: [
            {
              key: "Variable",
              title: "Status",
              topicLevel: "SpindleStatus",
              topic: [
                "Machine/Spindle/Realtime/Status",
                "Machine/Spindle/Realtime/TemperatureStatus",
                "Machine/Spindle/Realtime/VibrationStatus"
              ],
              id: uuidv4(),
              node: {},
              children: []
            },
            {
              key: "Variable",
              title: "Vibration",
              topicLevel: "SpindleVibration",
              topic: ["Machine/Spindle/Realtime/VibrationLevel"],
              id: uuidv4(),
              node: {}
            },
            {
              key: "Variable",
              title: "Speed",
              topicLevel: "SpindleSpeed",
              topic: ["Machine/Spindle/Realtime/Speed"],
              id: uuidv4(),
              node: {}
            },
            {
              key: "Variable",
              title: "Temperature",
              topicLevel: "SpindleTemperature",
              topic: ["Machine/Spindle/Realtime/Temperature"],
              id: uuidv4(),
              node: {}
            },
            ...loadActiveTimeNodes
          ]
        };

        result.children.push(spindleSection);
      }
    }
  }

  return result;
};

const addEnvironmentSensor = async (components, machine, result) => {
  if (components) {
    result = { ...components };

    let addSensor = false;

    try {
      const hasChannels = await streamApi.getChannels(machine.Id, "Environment/+");
      addSensor = hasChannels.length > 0;
    } catch (e) {
      console.error("[Error] addEnvironmentSensor error:", e);
    }

    if (addSensor) {
      const sensorSection = {
        key: "Component",
        title: "Sensor",
        topicLevel: "Sensor",
        topic: [],
        id: uuidv4(),
        node: {},
        children: [
          {
            key: "Component",
            title: Vue.i18n.translate("sensor_title.environment"),
            topicLevel: "SensorEnvironment",
            topic: [],
            id: uuidv4(),
            node: {},
            children: [
              {
                key: "Variable",
                title: "Humidity",
                topicLevel: "Humidity",
                topic: ["Environment/Humidity"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "Pressure",
                topicLevel: "Pressure",
                topic: ["Environment/Pressure"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "Temperature",
                topicLevel: "Temperature",
                topic: ["Environment/Temperature"],
                id: uuidv4(),
                node: {}
              }
            ]
          },
          {
            key: "Component",
            title: Vue.i18n.translate("sensor_title.consumption"),
            topicLevel: "SensorComponent",
            topic: [],
            id: uuidv4(),
            node: {},
            children: [
              {
                key: "Variable",
                title: "ActiveEnergy",
                topicLevel: "ActiveEnergy",
                topic: ["Machine/Consumption/ActiveEnergy"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "ActivePower",
                topicLevel: "ActivePower",
                topic: ["Machine/Consumption/ActivePower"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "CompressedAir",
                topicLevel: "CompressedAir",
                topic: ["Machine/Consumption/CompressedAir"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "ReactiveEnergy",
                topicLevel: "ReactiveEnergy",
                topic: ["Machine/Consumption/ReactiveEnergy"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "ReactivePower",
                topicLevel: "ReactivePower",
                topic: ["Machine/Consumption/ReactivePower"],
                id: uuidv4(),
                node: {}
              }
            ]
          },
          {
            key: "Component",
            title: Vue.i18n.translate("sensor_title.electrical_cabinet"),
            topicLevel: "SensorElectricalCabinet",
            topic: [],
            id: uuidv4(),
            node: {},
            children: [
              {
                key: "Variable",
                title: "Humidity",
                topicLevel: "Humidity",
                topic: ["Machine/ElectricalCabinet/Humidity"],
                id: uuidv4(),
                node: {}
              },
              {
                key: "Variable",
                title: "Temperature",
                topicLevel: "Temperature",
                topic: ["Machine/ElectricalCabinet/Temperature"],
                id: uuidv4(),
                node: {}
              }
            ]
          }
        ]
      };

      result.children.push(sensorSection);
    }
  }

  return result;
};

const markNodeVariant = (node, symbolicNames, variant) => {
  if (node) {
    const children = (node && node.children) || [];
    const symbolicName = node && node.node && node.node.symbolicName;
    const validNode = (symbolicNames || []).includes(symbolicName);

    if (node.key === "Component" || validNode) {
      node.variant = variant;
    }
    node.children = children.map(el => markNodeVariant(el, symbolicNames, variant));
  }

  return node;
};

const markNodesVariant = (machine, components) => {
  const result = components;
  const isCms = isCmsMachine(machine);

  if (isCms) {
    const cncProcessesNode = findNode("CncProcesses", components, {
      field: "topicLevel"
    });
    const operatingGroupsNode = findNode(["Machine/OperatingGroups"], components, {
      field: "topic",
      all: false
    });

    operatingGroupsNode.variant = "cms";
    operatingGroupsNode.children.forEach(operatingGroup => {
      const type = findNode(["Type"], operatingGroup, {
        field: "topicLevel",
        all: false
      });
      switch (type.node.value) {
        case "SPINDLE":
          markNodeVariant(operatingGroup, ["Load", "SpeedRate"], "cms-spindle", "cms-spindle");
          break;
        case "AWJ":
          markNodeVariant(operatingGroup, ["Load", "SpeedOverride", "SpeedRate"], "cms-awj");
          break;
        case "WJ":
          markNodeVariant(operatingGroup, ["SpeedRate"], "cms-wj");
          break;
      }
    });

    markNodeVariant(cncProcessesNode, ["Mode", "Status"], "cms");
  }

  return result;
};

const removeNodeFromComponents = (node, searchValue, field = "id") => {
  let result = node;

  if (node) {
    if (node[field] === searchValue) {
      result = undefined;
    } else if (node.children) {
      result.children = node.children
        .filter(el => {
          return el[field] !== searchValue;
        })
        .map(el => el && removeNodeFromComponents(el, searchValue, field));
    }
  }

  return result;
};

const createPath = ({ node, fatherPath = "", blacklist = [] }) => {
  let path = fatherPath;

  if (node) {
    const { title, children, topicLevel } = node;

    if (!blacklist.includes(topicLevel)) {
      path = (path && `${path}/${title}`) || title;
    }

    node.children = (children || []).map(child => {
      return createPath({ node: child, fatherPath: path, blacklist });
    });
  }

  return { ...(node || {}), path };
};

const descriptionFieldsFromChildNode = node => {
  const childNode = node && node.node;
  const key =
    (childNode && (childNode.name || childNode.browseName || childNode.symbolicName)) || "";
  const value = (childNode && childNode.value) || "";

  return { key, value };
};

const descriptionFromNode = (node, key, topicLevel) => {
  const children = (node && node.children) || [];
  const childrenFields = children
    .map(el => descriptionFieldsFromChildNode(el))
    .reduce((acc, el) => {
      if (!acc[el.key] && el.value) {
        acc[el.key] = el.value;
      }
      return acc;
    }, {});
  let result = "";
  const { Type, Model, Location } = childrenFields;

  if (Type) {
    result = `${Type}`;
  }

  if (Model && Location) {
    result = result.length > 0 ? `${result} (${Model}, ${Location})` : `${Model}, ${Location}`;
  } else {
    if (Model) {
      result = result.length > 0 ? `${result}, ${Model}` : `${Model}`;
    }

    if (Location) {
      result = result.length > 0 ? `${result}, ${Location}` : `${Location}`;
    }
  }

  result =
    result.length > 0
      ? `${topicLevel || key} - ${result}`
      : children.length === 0
      ? `${topicLevel || key}`
      : "";

  return result;
};

const fillDescription = ({ node, fatherDescription = "", blacklist = [] }) => {
  const { key, topicLevel, children } = node || {};
  const description = descriptionFromNode(node, key, topicLevel);
  let result = "";

  if (!blacklist.includes(topicLevel) && description) {
    result = (fatherDescription && `${fatherDescription}/${description}`) || description;
  }

  node.children = (children || []).map(child => {
    return fillDescription({ node: child, fatherDescription: result, blacklist });
  });

  return { ...node, description: result };
};

const rearrangeComponents = components => {
  const operatingGroupsNode = findNode(["Machine/OperatingGroups"], components, {
    field: "topic",
    all: false
  });

  const electricHeater = operatingGroupsNode.children.filter(operatingGroupsNodeChild => {
    const eletricHeaterChildren = findNode("ELECTRIC_HEATER", operatingGroupsNodeChild, {
      field: "value",
      originalNode: true,
      all: true
    });
    return eletricHeaterChildren.length > 0;
  });

  operatingGroupsNode.children = operatingGroupsNode.children.filter(operatingGroupsNodeChild => {
    return !electricHeater.includes(operatingGroupsNodeChild);
  });

  return {
    components,
    removedNodes: {
      electricHeater
    }
  };
};

const tableDataFromNode = (node, machine) => {
  const result = (node || []).map(el => {
    const operatingGroup = { value: el.node && el.node.name, topic: el.topic, machine };
    const elMap = el.children
      .map(elChild => {
        return {
          key: elChild.topicLevel,
          value: elChild.node && elChild.node.value,
          units: elChild.node && elChild.node.units,
          topic: elChild.topic
        };
      })
      .reduce((acc, newEl) => {
        acc[newEl.key] = {
          value: newEl.value,
          topic: newEl.topic,
          units: newEl.units,
          machine
        };
        return acc;
      }, {});
    return {
      OperatingGroup: operatingGroup,
      ...elMap
    };
  });

  return result;
};

export const extendComponentsWithAdditionalInfo = async (
  components,
  machine,
  enableSensorsSubscription
) => {
  let newComponents = undefined;
  const tableData = {
    electricHeater: []
  };

  try {
    if (enableSensorsSubscription) {
      newComponents = await addEnvironmentSensor(components, machine, newComponents);
      if (enableSpindleSensor) {
        newComponents = await addSpindleSensor(components, machine, newComponents);
      }
      newComponents = removeNodeFromComponents(newComponents, "Events", "topicLevel");
    } else {
      newComponents = components;
    }

    let resultRC = {};
    if (getFamily(machine).toLowerCase() === "tfo") {
      resultRC = rearrangeComponents(newComponents, machine);
      tableData.electricHeater = tableDataFromNode(resultRC.removedNodes.electricHeater, machine);
    }

    newComponents = markNodesVariant(machine, resultRC.components || newComponents);
    newComponents = createPath({ node: newComponents, blacklist: ["ModelDesign"] });
    newComponents = fillDescription({ node: newComponents, blacklist: ["ModelDesign"] });
  } catch (e) {
    console.error("[Error] extendComponentsWithAdditionalInfo error:", e);
  }

  return {
    components: newComponents,
    tableData
  };
};
